import { useEffect, useState } from "react";
import { Avatar, BalancePill, Badge, Button, Card, LevelBadge, ProgressBar } from "@jaiho/ui";
import type { HomeResponse } from "@jaiho/shared";
import { useAuth } from "../state/AuthContext";
import { claimDailyReward, getHome } from "../api/endpoints";
import { formatActivityEvent } from "../lib/activity-text";
import { BrandLogo } from "../components/BrandLogo";
import { GameCard } from "../components/GameCard";
import "./HomeScreen.css";

export function HomeScreen() {
  const { patchMe } = useAuth();
  const [home, setHome] = useState<HomeResponse | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    getHome().then(setHome).catch(() => setLoadError(true));
  }, []);

  async function handleClaim() {
    setClaiming(true);
    try {
      const result = await claimDailyReward();
      setHome((prev) =>
        prev
          ? {
              ...prev,
              wallet: { balance: result.balance },
              progression: result.progression,
              streak: result.streak,
              dailyReward: { ...prev.dailyReward, claimedToday: true },
            }
          : prev
      );
      patchMe({ wallet: { balance: result.balance }, progression: result.progression, streak: result.streak });
    } finally {
      setClaiming(false);
    }
  }

  if (loadError) {
    return (
      <div className="jaiho-home jaiho-home--error">
        <Card>Couldn't load your lobby. Pull to refresh or reopen the app.</Card>
      </div>
    );
  }

  if (!home) {
    return (
      <div className="jaiho-home">
        <div className="jaiho-skeleton jaiho-skeleton--header" />
        <div className="jaiho-skeleton jaiho-skeleton--card" />
        <div className="jaiho-skeleton jaiho-skeleton--card" />
      </div>
    );
  }

  return (
    <div className="jaiho-home">
      <header className="jaiho-home__header">
        <div className="jaiho-home__identity">
          <div className="jaiho-home__avatar-wrap">
            <Avatar name={home.profile.displayName} src={home.profile.avatarUrl} size={44} />
            <LevelBadge level={home.progression.level} size="sm" />
          </div>
          <div>
            <div className="jaiho-home__brand">
              <BrandLogo size={16} />
              Jaiho91 Social
            </div>
            <div className="jaiho-home__greeting">Hi, {home.profile.displayName}</div>
          </div>
        </div>
        <BalancePill balance={home.wallet.balance} />
      </header>

      <Card className="jaiho-home__daily-card">
        <div className="jaiho-home__daily-top">
          <div>
            <div className="jaiho-home__daily-title">Daily Reward</div>
            <div className="jaiho-home__daily-sub">
              {home.dailyReward.amountCredits.toLocaleString("en-IN")} credits + {home.dailyReward.amountXp} XP
            </div>
          </div>
          <Button onClick={handleClaim} disabled={claiming || home.dailyReward.claimedToday}>
            {home.dailyReward.claimedToday ? "Claimed" : claiming ? "Claiming…" : "Claim"}
          </Button>
        </div>
        {home.streak.currentStreak > 0 && (
          <div className="jaiho-home__streak">
            <span className="jaiho-home__streak-flame" aria-hidden />
            {home.streak.currentStreak} game-day streak
            {home.streak.longestStreak > home.streak.currentStreak && ` · best ${home.streak.longestStreak}`}
          </div>
        )}
      </Card>

      <Card className="jaiho-home__level-card">
        <div className="jaiho-home__level-top">
          <span className="jaiho-home__level-label">Level {home.progression.level}</span>
          <span className="jaiho-home__level-xp">{home.progression.totalXp.toLocaleString("en-IN")} XP</span>
        </div>
        <ProgressBar
          value={home.progression.progressRatio}
          label={`${home.progression.xpRemainingToNextLevel} XP to Level ${home.progression.level + 1}`}
        />
      </Card>

      <section className="jaiho-home__section">
        <div className="jaiho-home__section-title">Featured Games</div>
        <div className="jaiho-home__games-grid">
          {home.featuredGames.map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>
      </section>

      {home.missions.length > 0 && (
        <section className="jaiho-home__section">
          <div className="jaiho-home__section-title">Today's Missions</div>
          <Card className="jaiho-home__missions">
            {home.missions.map((mission) => (
              <div key={mission.key} className="jaiho-home__mission-row">
                <div className={`jaiho-home__mission-check${mission.completed ? " jaiho-home__mission-check--done" : ""}`}>
                  {mission.completed ? "✓" : ""}
                </div>
                <div className="jaiho-home__mission-copy">
                  <div className="jaiho-home__mission-name">{mission.name}</div>
                  <div className="jaiho-home__mission-desc">{mission.description}</div>
                </div>
                {mission.rewardXp > 0 && <Badge tone="accent">+{mission.rewardXp} XP</Badge>}
              </div>
            ))}
          </Card>
        </section>
      )}

      <section className="jaiho-home__section">
        <div className="jaiho-home__section-title">Live at Jaiho91</div>
        <Card className="jaiho-home__live">
          <div className="jaiho-home__online">
            <span className="jaiho-home__online-dot" aria-hidden />
            {home.onlineCount ?? "—"} {home.onlineCount === 1 ? "player" : "players"} online now
          </div>
          {home.recentActivity.length > 0 ? (
            <ul className="jaiho-home__activity-list">
              {home.recentActivity.slice(0, 5).map((event) => (
                <li key={event.id}>{formatActivityEvent(event)}</li>
              ))}
            </ul>
          ) : (
            <div className="jaiho-home__activity-empty">No activity yet — be the first to level up.</div>
          )}
        </Card>
      </section>

      {home.announcement && (
        <section className="jaiho-home__section">
          <Card className="jaiho-home__announcement">
            <div className="jaiho-home__announcement-title">{home.announcement.title}</div>
            <div>{home.announcement.body}</div>
          </Card>
        </section>
      )}
    </div>
  );
}
